import { notification } from 'antd';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import {
  useGetVariantDetailQuery,
  useGetVariantFrequenciesQuery,
  useUpdateMaxcAFScoringMutation,
} from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import { MaxAFScoring } from '../../redux';
import {
  calculateEstimatedAf,
  calculateMaxcAc,
  calculateMaxcAf,
} from '../../utils';

interface FormValues {
  phenotype_id?: number;
  inheritance?: string;
  prevalence?: string;
  allelic_heterogeneity?: number;
  genetic_heterogeneity?: number;
  penetrance?: number;
}

interface UseMaximumCredibleModalLogicProps {
  closeModal: () => void;
  record?: MaxAFScoring;
}

/**
 * Custom hook that encapsulates all the business logic for MaximumCredibleModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useMaximumCredibleModalLogic = ({
  closeModal,
  record,
}: UseMaximumCredibleModalLogicProps) => {
  const {
    isLoading,
    setIsLoading,
    user,
    isReadOnly,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: NOTIFICATION_MESSAGES.MAX_CREDIBLE_AF_UPDATED,
    stageType: VARIANT_STAGE_TYPES.FREQUENCY_DATA,
  });

  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Mutation hook returns a [trigger, result] tuple
  const [updateMaxcAFScoringMutation] = useUpdateMaxcAFScoringMutation();

  const initialValues: FormValues = useMemo(
    () => ({
      phenotype_id: record?.phenotype?.id,
      gene_id: record?.gene?.id,
      inheritance: record?.inheritance,
      prevalence: record?.prevalence,
      allelic_heterogeneity: record?.allelic_heterogeneity,
      genetic_heterogeneity: record?.genetic_heterogeneity,
      penetrance: record?.penetrance ? record.penetrance * 100 : 0,
    }),
    [record],
  );

  // Read frequencies straight from the query hook's cache rather than a selector
  const { data: variantFrequencies } = useGetVariantFrequenciesQuery(
    { variant_id: variant?.id },
    { skip: !variant?.id },
  );
  const populationSize = useMemo(() => {
    if (
      variantFrequencies?.frequencies &&
      variantFrequencies.frequencies.length > 0
    ) {
      // Use the first population's allele_number as the total population size
      const alleleNumber = parseInt(
        variantFrequencies.frequencies[0].allele_number,
      );
      return isNaN(alleleNumber) ? 0 : alleleNumber;
    }
    return 0;
  }, [variantFrequencies]);

  /**
   * Calculate estimated allele frequency based on form values
   *
   * @param values - Form values containing inheritance and prevalence
   * @returns Calculated estimated_af value or 0 if inputs are invalid
   */
  const calculateEstimatedAfFromValues = useCallback(
    (values: FormValues): number => {
      return calculateEstimatedAf(
        values.inheritance || '',
        values.prevalence || '',
      );
    },
    [],
  );

  const modalOpts = createModalOpts(
    'Calculate the maximum credible population allele frequency for the phenotype of interest.',
  );

  const handleFormSubmit = async (values: any) => {
    if (isLoading || isReadOnly) {
      return;
    }

    if (user && variant?.id && record?.id) {
      try {
        setIsLoading(true);

        const requestData = {
          inheritance: values.inheritance,
          prevalence: values.prevalence,
          allelic_heterogeneity: values.allelic_heterogeneity,
          genetic_heterogeneity: values.genetic_heterogeneity,
          penetrance: values.penetrance / 100,
          gene_id: record.gene.id,
          phenotype_id: record.phenotype.id,
        };

        await updateMaxcAFScoringMutation({
          maxcafscoring_id: record.id,
          variant_id: variant.id,
          requestData,
        }).unwrap();

        // invalidatesTags marks MaxAFScorings + VariantStages stale, so those
        // queries refetch automatically

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: NOTIFICATION_MESSAGES.MAX_CREDIBLE_AF_UPDATED,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
        closeModal();
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  return {
    isLoading,
    isReadOnly,
    user,
    variant,
    stageInfo,
    initialValues,
    populationSize,
    modalOpts,
    record,
    getIsSaveDisabled,
    handleFormSubmit,
    calculateEstimatedAfFromValues,
    calculateMaxcAc,
    calculateMaxcAf,
  };
};

export default useMaximumCredibleModalLogic;
